import {
  addDays,
  addMonths,
  addWeeks,
  differenceInDays,
  eachDayOfInterval,
  endOfMonth,
  isAfter,
  isBefore,
  parseISO,
  setDate,
  startOfDay,
} from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

interface ProrationParams {
  tuitionFee: number;
  tuitionBillingCycle: string;
  startDate?: string;
  endDate?: string;
  billingDay?: number;
  billingFrequencyDays?: number;
  enrollmentDate?: Date; // Optional - defaults to today
  prorationBasis?: 'calendar' | 'session';
  classDays?: Array<string>;
  timeZone?: string;
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function countScheduledMeetings(
  startDate: Date,
  endDate: Date,
  classDays: Array<string>,
): number {
  if (isAfter(startDate, endDate)) return 0;

  const scheduledDays = new Set(classDays.map((day) => day.slice(0, 3)));

  return eachDayOfInterval({ start: startDate, end: endDate }).filter((date) =>
    scheduledDays.has(DAY_NAMES[date.getDay()]),
  ).length;
}

function calculateMonthlySessionProration({
  tuitionFee,
  effectiveStartDate,
  classDays,
}: {
  tuitionFee: number;
  effectiveStartDate: Date;
  classDays: Array<string>;
}): number {
  const periodEnd = endOfMonth(effectiveStartDate);
  const periodStart = new Date(
    effectiveStartDate.getFullYear(),
    effectiveStartDate.getMonth(),
    1,
  );
  const totalMeetings = countScheduledMeetings(
    periodStart,
    periodEnd,
    classDays,
  );
  const remainingMeetings = countScheduledMeetings(
    effectiveStartDate,
    periodEnd,
    classDays,
  );

  if (totalMeetings === 0) return tuitionFee;

  return Math.max(
    0,
    Math.round((tuitionFee * remainingMeetings * 100) / totalMeetings) / 100,
  );
}

/**
 * Calculate prorated tuition amount (frontend estimation)
 * Note: This is an estimate. The backend calculates the actual prorated amount.
 */
export function calculateProratedTuition(params: ProrationParams): number {
  const {
    tuitionFee,
    tuitionBillingCycle,
    startDate,
    endDate,
    billingDay,
    billingFrequencyDays,
    enrollmentDate = new Date(),
    prorationBasis = 'calendar',
    classDays = [],
    timeZone,
  } = params;

  // If proration is disabled or one-time billing, return full fee
  if (
    tuitionBillingCycle === 'One Time' ||
    tuitionBillingCycle === 'one-time'
  ) {
    return tuitionFee;
  }

  // Transfer/refetch flows can briefly expose incomplete class billing data.
  // Fall back to full tuition instead of crashing the route.
  if (!startDate) {
    return tuitionFee;
  }

  // Convert to UTC dates for accurate calculation (matching backend logic)
  const enrollmentDateKey = formatInTimeZone(
    enrollmentDate,
    timeZone ?? 'UTC',
    'yyyy-MM-dd',
  );
  const todayUTC = startOfDay(parseISO(enrollmentDateKey));
  const classStartUTC = startOfDay(parseISO(startDate.slice(0, 10)));

  // If class hasn't started yet, charge full amount
  if (isBefore(todayUTC, classStartUTC)) {
    return tuitionFee;
  }

  const effectiveStartDate = isAfter(todayUTC, classStartUTC)
    ? todayUTC
    : classStartUTC;

  if (tuitionBillingCycle === 'weekly') {
    // Weekly billing - estimate based on days remaining in week
    const DAYS_IN_WEEK = 7;
    const dailyRate = tuitionFee / DAYS_IN_WEEK;

    // Find next billing date (simplified - assume billing every 7 days from class start)
    let nextBillingDate = addWeeks(classStartUTC, 1);
    while (
      isBefore(nextBillingDate, effectiveStartDate) ||
      nextBillingDate.getTime() === effectiveStartDate.getTime()
    ) {
      nextBillingDate = addWeeks(nextBillingDate, 1);
    }

    // nextBillingDate is exclusive (start of next billing period)
    // effectiveStartDate is inclusive (enrollment day)
    // differenceInDays() correctly calculates days from inclusive start to exclusive end
    const daysToCharge = differenceInDays(nextBillingDate, effectiveStartDate);
    return Math.max(0, Math.round(dailyRate * daysToCharge * 100) / 100);
  } else if (tuitionBillingCycle === 'bi-weekly') {
    // Bi-weekly billing - estimate based on days remaining in 2-week period
    const DAYS_IN_TWO_WEEKS = 14;
    const dailyRate = tuitionFee / DAYS_IN_TWO_WEEKS;

    let nextBillingDate = addWeeks(classStartUTC, 2);
    while (
      isBefore(nextBillingDate, effectiveStartDate) ||
      nextBillingDate.getTime() === effectiveStartDate.getTime()
    ) {
      nextBillingDate = addWeeks(nextBillingDate, 2);
    }

    // nextBillingDate is exclusive (start of next billing period)
    // effectiveStartDate is inclusive (enrollment day)
    // differenceInDays() correctly calculates days from inclusive start to exclusive end
    const daysToCharge = differenceInDays(nextBillingDate, effectiveStartDate);
    return Math.max(0, Math.round(dailyRate * daysToCharge * 100) / 100);
  } else if (
    tuitionBillingCycle === 'every-x-days' &&
    billingFrequencyDays &&
    billingFrequencyDays >= 1
  ) {
    const dailyRate = tuitionFee / billingFrequencyDays;

    let nextBillingDate = classStartUTC;
    while (
      isBefore(nextBillingDate, effectiveStartDate) ||
      nextBillingDate.getTime() === effectiveStartDate.getTime()
    ) {
      nextBillingDate = addDays(nextBillingDate, billingFrequencyDays);
    }

    const daysToCharge = differenceInDays(nextBillingDate, effectiveStartDate);
    return Math.max(0, Math.round(dailyRate * daysToCharge * 100) / 100);
  } else if (
    tuitionBillingCycle === 'monthly' &&
    prorationBasis === 'session' &&
    classDays.length > 0
  ) {
    return calculateMonthlySessionProration({
      tuitionFee,
      effectiveStartDate,
      classDays,
    });
  } else if (tuitionBillingCycle === 'monthly' && billingDay) {
    // Monthly billing - requires billingDay
    // Find first billing date
    let firstBillingDate = setDate(classStartUTC, billingDay);
    if (isBefore(firstBillingDate, classStartUTC)) {
      firstBillingDate = addMonths(firstBillingDate, 1);
    }

    // Find next billing date after effective start
    let nextBillingDate = firstBillingDate;
    let attempts = 0;
    while (
      (isBefore(nextBillingDate, effectiveStartDate) ||
        nextBillingDate.getTime() === effectiveStartDate.getTime()) &&
      attempts < 12
    ) {
      nextBillingDate = addMonths(nextBillingDate, 1);
      attempts++;
    }

    if (attempts >= 12) {
      return tuitionFee; // Fallback to full amount
    }

    // Calculate billing end date
    let billingEndDate = nextBillingDate;
    if (endDate) {
      const classEnd = startOfDay(parseISO(endDate.slice(0, 10)));
      if (isBefore(classEnd, nextBillingDate)) {
        billingEndDate = addDays(classEnd, 1);
      }
    }

    // Calculate actual days from effective start to billing end date
    // billingEndDate is exclusive (either nextBillingDate or classEndDate + 1)
    // effectiveStartDate is inclusive (enrollment day)
    // differenceInDays() correctly calculates days from inclusive start to exclusive end
    // Use the actual calculated days instead of capping at 30
    const totalDays = differenceInDays(billingEndDate, effectiveStartDate);

    if (totalDays <= 0) {
      return 0;
    }

    // Use the month where most of the period falls for daily rate calculation
    const midPoint = addDays(effectiveStartDate, Math.floor(totalDays / 2));
    const daysInMonth = new Date(
      midPoint.getFullYear(),
      midPoint.getMonth() + 1,
      0,
    ).getDate();
    const dailyRate = tuitionFee / daysInMonth;

    return Math.max(0, Math.round(dailyRate * totalDays * 100) / 100);
  }

  // Default: return full fee if we can't calculate
  return tuitionFee;
}
